import { useRef, useState, type ChangeEvent } from "react";
import { UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { download, request, saveBlob } from "./api";
import { Button } from "./components";
import type { InventorySkuOption } from "./types";
import "./inventory-sheet.css";

type Preview = {
  sheetName: string; headerRow: number; columns: string[];
  rows: { rowNumber: number; code: string; skuCode?: string; productCode?: string; genericCode?: string;
    name: string; specification: string; unit?: string;
    quantityText: string; quantity: number | null; sku?: InventorySkuOption; error?: string; warnings?: string[] }[];
  lines: (InventorySkuOption & { quantity: number })[];
  errors: string[]; warnings?: string[]; valid: boolean; totalQuantity: number;
};

export function InventorySheetImport({ existingCount, disabled, onApply, onBusyChange }: {
  existingCount: number; disabled: boolean;
  onApply: (lines: (InventorySkuOption & { quantity: number })[]) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const canApply = preview?.valid && preview.errors.length === 0 && !preview.rows.some((row) => row.error);

  async function importFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPreview(null); setError(""); setMessage(""); setFilename(file.name);
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) { setError("支持 .xlsx、.xls 和 .csv 表格"); return; }
    if (file.size > 8 * 1024 * 1024) { setError("表格不能超过 8 MB"); return; }
    setBusy(true); onBusyChange(true);
    try {
      const data = new FormData();
      data.append("file", file);
      setPreview(await request<Preview>("/api/v1/admin/inventory/sheet/preview", { method: "POST", body: data }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "表格识别失败，请重试");
    } finally { setBusy(false); onBusyChange(false); }
  }

  async function downloadTemplate() {
    setDownloading(true); setError("");
    try {
      saveBlob(await download("/api/v1/admin/inventory/sheet/template"), "库存单据明细模板.xlsx");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "模板下载失败"); }
    finally { setDownloading(false); }
  }

  return (
    <section className="inventory-sheet-import" aria-label="表格导入">
      <header>
        <div><h3>表格导入 · 自动识别</h3><p>Excel / CSV · 自动识别 SKU 编码、商品编码和数量</p></div>
        <div className="inventory-sheet-actions">
          <Button type="button" variant="ghost" onClick={() => void downloadTemplate()} loading={downloading} disabled={disabled}>
            <DownloadSimple /> 下载模板
          </Button>
          <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()} loading={busy} disabled={disabled}>
            <UploadSimple /> 导入表格
          </Button>
          <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" aria-label="导入库存明细表格" hidden disabled={busy || disabled} onChange={(event) => void importFile(event)} />
        </div>
      </header>
      <p className="inventory-sheet-hint">以 SKU 编码或商品编码为准。名称、规格不一致时会提示，并采用系统档案；编码不存在或冲突需修正，仅提供商品编码且对应多个 SKU 时需补充 SKU 编码，单位不符需先换算数量。没有编码时仅按名称和规格精确唯一匹配，请核对。点击“确认登记”才更新库存。</p>
      {busy && <p role="status">正在识别 {filename}…</p>}
      {error && <p className="inline-error" role="alert">{error}</p>}
      {message && <p className="inventory-sheet-success" role="status">{message}</p>}
      {preview && (
        <div className="inventory-sheet-preview">
          <p><b>{filename}</b>{preview.sheetName && <> · {preview.sheetName} · 表头第 {preview.headerRow} 行</>}</p>
          {preview.columns.length > 0 && <p className="inventory-sheet-columns">已识别：{preview.columns.join("、")}</p>}
          {preview.errors.map((item) => <p className="inline-error" role="alert" key={item}>{item}</p>)}
          {(preview.warnings?.length ?? 0) > 0 && <ul className="inventory-sheet-warnings" aria-label="库存识别提示">
            {preview.warnings?.map((item, index) => <li key={index}>{item}</li>)}
          </ul>}
          {preview.rows.length > 0 && <>
            <div className="inventory-sheet-table-wrap">
              <table aria-label="表格识别结果">
                <thead><tr><th>表格行</th><th>商品 / SKU</th><th>数量</th><th>识别结果</th></tr></thead>
                <tbody>{preview.rows.map((row) => <tr key={row.rowNumber}>
                  <td>{row.rowNumber}</td>
                  <td>
                    <b>{row.sku?.productName || row.name || row.skuCode || row.productCode || row.code || "未填写商品"}</b>
                    <small>{row.sku ? `SKU：${row.sku.code} · 商品编码：${row.sku.productCode} · ${row.sku.specification}` : [row.skuCode || row.productCode || row.code, row.specification].filter(Boolean).join(" · ")}</small>
                    {row.sku && row.name && row.name !== row.sku.productName && <small className="inventory-sheet-source">原表名称：{row.name}</small>}
                    {row.sku && row.specification && row.specification !== row.sku.specification && <small className="inventory-sheet-source">原表规格：{row.specification}</small>}
                    {row.sku && row.unit && row.unit !== row.sku.unit && <small className="inventory-sheet-source">原表单位：{row.unit}</small>}
                  </td>
                  <td>{row.quantity ?? row.quantityText} {row.sku?.unit}</td>
                  <td className={row.error ? "inventory-sheet-error" : row.warnings?.length ? "inventory-sheet-warning" : "inventory-sheet-success"}>
                    {row.error || (row.warnings?.length ? "已匹配，请核对提示" : "已匹配")}
                    {(row.warnings?.length ?? 0) > 0 && <ul className="inventory-sheet-row-warnings" aria-label={`第 ${row.rowNumber} 行提示`}>
                      {row.warnings?.map((warning, index) => <li key={index}>{warning}</li>)}
                    </ul>}
                  </td>
                </tr>)}</tbody>
              </table>
            </div>
            <div className="inventory-sheet-review">
              <span>{preview.rows.length} 行 · 合并为 {preview.lines.length} 个 SKU{preview.rows.some((row) => row.error) && ` · ${preview.rows.filter((row) => row.error).length} 行需修正`}
                {preview.rows.some((row) => row.warnings?.length) && ` · ${preview.rows.filter((row) => row.warnings?.length).length} 行有提示`}
                <small>{existingCount ? `填入后将替换本单现有 ${existingCount} 项明细` : "填入后仍可修改数量或移除明细"}</small>
              </span>
              <Button type="button" disabled={!canApply || disabled || busy} onClick={() => {
                onApply(preview.lines);
                setMessage(`已填入 ${preview.lines.length} 个 SKU，请核对本单明细后确认登记。`);
                setPreview(null);
              }}>用识别结果填入明细</Button>
            </div>
            {!canApply && <p className="inventory-sheet-hint">请修正表格中的错误并重新上传。黄色提示可核对后继续，编码与单位等错误必须先处理。</p>}
          </>}
        </div>
      )}
    </section>
  );
}
